import { forwardRef } from 'react';
import { Button, Card, CheckBox, Message, Spinner } from '@iwoca/orion';
import { FormProvider } from 'react-hook-form';
import { TImperativeHandleSignup } from 'types';
import { ClientDetails } from '../ClientDetails/ClientDetails';
import { CompanyDetails } from '../CompanyDetails/CompanyDetails';
import { DevTimeSaver } from '../DevTimeSaver/DevTimeSaver';
import { LoanApplication } from '../LoanApplication/LoanApplication';
import { RefinancingDebts } from '../RefinancingDebts/RefinancingDebts';
import { useSignUpForm } from './SignupForm.hooks';
import styles from './SignupForm.module.css';

type TProps = {
  setSaveForLaterLoading?: (data: boolean) => void;
};

export const SignupForm = forwardRef<TImperativeHandleSignup, TProps>(
  ({ setSaveForLaterLoading }, ref) => {
    const {
      isDisabled,
      signupError,
      onSubmit,
      onError,
      isSubmitting,
      methods,
      stateHasLoaded,
      applicationHasLoaded,
      showRefinancing,
    } = useSignUpForm({ ref, setSaveForLaterLoading });

    if (!stateHasLoaded || !applicationHasLoaded) {
      return (
        <div className={styles.container}>
          <Card className={styles.form}>
            <Spinner
              className={styles.spinner}
              size="large"
              data-testid="loading-spinner"
            />
          </Card>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <FormProvider {...methods}>
          <form
            className={styles.form}
            onSubmit={methods.handleSubmit(onSubmit, onError)}
          >
            <CompanyDetails />
            <ClientDetails />
            <LoanApplication />
            {showRefinancing && <RefinancingDebts disabled={isDisabled} />}
            <div className={styles.privacyPolicyAgreement}>
              <p>
                We’ll use your client's (and other directors or beneficial
                owners of their company, as well as anyone providing a personal
                guarantee) personal information in a few ways:
              </p>
              <ul className={styles.personalInfoList}>
                <li>
                  <strong>
                    We’ll share it with credit reference agencies to run soft
                    credit and identity checks
                  </strong>{' '}
                  - this doesn’t affect your client's (or others') personal
                  credit score.
                </li>
                <li>
                  <strong>We’ll share it with fraud prevention agencies</strong>{' '}
                  - if we do spot any fraud, your client or others could be
                  refused services, finance, or employment.
                </li>
                <li>
                  <strong>We might record your client’s calls with us.</strong>
                </li>
              </ul>
              <p>
                Remember to make sure your client and the others (directors,
                beneficial owners or anyone providing a personal guarantee) are
                aware of this before you click submit application. Please also
                provide them with a copy of our{' '}
                <a
                  href="https://www.iwoca.co.uk/introducer-privacy-policy/"
                  target="_blank"
                  rel="noreferrer"
                >
                  privacy policy
                </a>
                . By ticking the below box and clicking submit application you
                confirm that all of the above has happened. The data controller
                is iwoca Ltd.
              </p>
            </div>
            <div className={styles.footerCtas}>
              <CheckBox
                className={styles.privacyPolicyCheckbox}
                {...methods.register('applicant.privacyPolicyAgreement')}
                label="I’ve read and agreed to all terms & conditions"
                data-testid="privacyPolicyAgreement"
                disabled={isDisabled}
              />
              {methods.formState.errors.applicant?.privacyPolicyAgreement
                ?.message && (
                <Message
                  message={
                    methods.formState.errors.applicant?.privacyPolicyAgreement
                      ?.message
                  }
                  type="error"
                />
              )}
              <div className={styles.signupFormSubmit}>
                <Button
                  variant="primary"
                  size="large"
                  type="submit"
                  loading={isSubmitting}
                  disabled={isDisabled}
                >
                  Submit application
                </Button>
              </div>
              {signupError && (
                <Message
                  className={styles.errorMessage}
                  message={signupError}
                  type="error"
                />
              )}
            </div>
          </form>
          <DevTimeSaver />
        </FormProvider>
      </div>
    );
  },
);

SignupForm.displayName = 'SignupForm';
